import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Watermark from "@/components/app/Watermark";
import AntiCopyGuard from "@/components/app/AntiCopyGuard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, ShieldCheck, Smartphone, MessageCircle, LogOut, Camera } from "lucide-react";
import { toast } from "sonner";
import { formatIST } from "@/lib/time";

function monogram(str) {
  const s = (str || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/);
  const initials = parts.length >= 2 ? parts[0][0] + parts[1][0] : s.slice(0, 2);
  return initials.toUpperCase();
}

function hueFor(str) {
  let h = 0; for (const c of (str || "x")) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

function AvatarBlock({ user, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const initials = monogram(user.name || user.email);
  const hue = hueFor(user.email);
  const bg = `hsl(${hue}, 55%, 35%)`;
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      await api.post("/profile/avatar", { image_url: data.url });
      onUpdated(data.url); toast.success("Avatar updated");
    } catch (err) { toast.error(err.message || "Failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="relative flex-shrink-0" data-testid="profile-avatar-block">
      {user.avatar_url ? (
        <img src={user.avatar_url} alt="Avatar" draggable={false}
          className="w-24 h-24 rounded-full object-cover border-4 shadow-lg"
          style={{ borderColor: "#F5C242" }} />
      ) : (
        <div className="w-24 h-24 rounded-full flex items-center justify-center border-4 shadow-lg font-display text-3xl text-white"
          style={{ background: bg, borderColor: "#F5C242" }} data-testid="profile-monogram">
          {initials}
        </div>
      )}
      <label className={`absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center shadow cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
        style={{ background: "#F5C242", color: "#1A1200" }} title="Change avatar">
        <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="avatar-upload-input" />
        <Camera size={16} />
      </label>
    </div>
  );
}

export default function Profile({ user, onLogout }) {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/profile").then(r => setData(r.data)); }, []);
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ color: "#B4841F" }}>Loading…</div>;
  const { user: u, outlet, terms, suggestions, stats } = data;
  return (
    <AntiCopyGuard>
      <div className="theme-viewer min-h-screen" style={{ background: "var(--viewer-bg)", color: "var(--viewer-text)" }}>
        <Watermark email={u.email} />
        <header className="sticky top-0 z-30 border-b" style={{ background: "rgba(253,251,247,0.95)", backdropFilter: "blur(10px)", borderColor: "#e2dccb" }}>
          <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
            <button onClick={() => nav(u.role === "admin" ? "/admin" : "/viewer")}
              className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest" style={{ color: "#6b5a45" }}
              data-testid="profile-back-btn">
              <ArrowLeft size={14} /> Back
            </button>
            <div className="font-display text-xl" style={{ color: "#2b1a0d" }}>Partner Profile</div>
            <button onClick={onLogout} className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest" style={{ color: "#6b5a45" }}
              data-testid="profile-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </header>

        <main className="relative z-20 max-w-4xl mx-auto px-6 py-8 space-y-6">
          <section className="rounded-2xl border bg-white p-6 md:p-8 flex items-start gap-5" style={{ borderColor: "#e2dccb" }}>
            <AvatarBlock user={u} onUpdated={(url) => setData({ ...data, user: { ...u, avatar_url: url } })} />
            <div className="flex-1 min-w-0">
              <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "#8a5a1a" }}>Franchisee</div>
              <h1 className="font-display text-4xl truncate" data-testid="profile-name">{u.name || u.email}</h1>
              <div className="mt-2 text-sm" style={{ color: "#6b5a45" }}>{u.email}{u.phone && <> · {u.phone}</>}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge className={u.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{u.active ? "Active" : "Revoked"}</Badge>
                <Badge variant="outline" className="uppercase text-[10px]">{u.role}</Badge>
                {u.device_bound && <Badge className="bg-[#F5C242]/30" style={{ color: "#8a5a1a" }}><Smartphone size={11} className="mr-1" />Device bound</Badge>}
              </div>
            </div>
          </section>

          <div className="grid md:grid-cols-2 gap-6">
            <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e2dccb" }}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} style={{ color: "#B4841F" }} />
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase" style={{ color: "#8a5a1a" }}>Assigned Outlet</div>
              </div>
              {outlet ? (
                <>
                  <div className="font-display text-2xl" data-testid="profile-outlet-name">{outlet.name}</div>
                  <div className="mt-2 text-xs font-mono2" style={{ color: "#6b5a45" }}>
                    {outlet.lat?.toFixed(6)}, {outlet.lng?.toFixed(6)}
                  </div>
                  <div className="mt-1 text-sm">Geofence radius: <b>{outlet.radius_m}m</b></div>
                </>
              ) : (
                <div className="text-sm" style={{ color: "#7a6349" }}>No outlet assigned yet.</div>
              )}
            </section>

            <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e2dccb" }}>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} style={{ color: "#B4841F" }} />
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase" style={{ color: "#8a5a1a" }}>T&C Status</div>
              </div>
              {terms ? (
                <>
                  <div className="text-sm">Signed as <b data-testid="profile-terms-name">{terms.franchisee_name || "—"}</b></div>
                  <div className="mt-1 text-xs font-mono2" style={{ color: "#6b5a45" }}>
                    v{terms.version} · {formatIST(terms.accepted_at)}
                  </div>
                  <div className="mt-1 text-xs font-mono2" style={{ color: "#6b5a45" }}>IP {terms.ip}</div>
                </>
              ) : (
                <div className="text-sm" style={{ color: "#c0392b" }}>Terms not yet acknowledged.</div>
              )}
            </section>
          </div>

          <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#e2dccb" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} style={{ color: "#B4841F" }} />
                <div className="font-mono2 text-[10px] tracking-[0.3em] uppercase" style={{ color: "#8a5a1a" }}>Past Suggestions</div>
              </div>
              <div className="text-xs font-mono2 uppercase tracking-widest" style={{ color: "#6b5a45" }}>
                {stats.total_suggestions} total · {stats.open_suggestions} open
              </div>
            </div>
            {suggestions.length === 0 ? (
              <div className="text-sm" style={{ color: "#7a6349" }}>You haven&apos;t sent any suggestions yet.</div>
            ) : (
              <ul className="divide-y" style={{ borderColor: "#e6dfcc" }}>
                {suggestions.map(s => (
                  <li key={s.id} className="py-3" data-testid={`profile-sug-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-medium">{s.item_name}
                          {s.ingredient_name && <span className="text-xs ml-2" style={{ color: "#7a6349" }}>· {s.ingredient_name}</span>}
                        </div>
                        <div className="text-sm mt-1 whitespace-pre-wrap" style={{ color: "#3d2a19" }}>{s.message}</div>
                        {s.replies && s.replies.length > 0 && (
                          <div className="mt-2 pl-3 border-l-2 space-y-2" style={{ borderColor: "#F5C242" }}>
                            {s.replies.map(r => (
                              <div key={r.id} className="text-sm">
                                <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
                                  {r.author_role === "admin" ? "🛡 Admin" : r.author_email} · {formatIST(r.created_at)}
                                </div>
                                <div style={{ color: "#2b1a0d" }}>{r.message}</div>
                                {r.image_url && (
                                  <a href={r.image_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-1">
                                    <img src={r.image_url} alt="Reply attachment" className="max-h-32 rounded border" style={{ borderColor: "#e2dccb" }} draggable={false} />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Badge className={s.status === "open" ? "bg-[#F5C242] text-black" : "bg-emerald-100 text-emerald-700"}>
                        {s.status}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </AntiCopyGuard>
  );
}
